import { debounce, isNil } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sentinelActions } from '../../redux/sentinel/actions';
import { createSentinelSearchDataByQuerySelector } from '../../redux/sentinel/selectors';
import { SearchResponse } from '../types';
import {
  SentinelQueryEvaluation,
  type DqSocketContextData,
  type DqSocketRequestOptions,
  type InferSocketResponse,
  type SentinelKey,
  type SentinelQueryEvaluator,
} from './types';
import { useSocket } from './useSocket';

/**
 * Performs a search request on a collection of objects.
 * @param Context The socket context to use for performing the request.
 * @param tag The collection of objects to search in.
 * @param query The query to perform. Note that this must be reference stable!
 * @param queryEvaluator A function that takes a single entity and the query that was used to
 * fetch this collection, and returns whether or not the entity would be included in the results.
 * This is called each time we receive a "created" or "updated" websocket event, and shortcuts the
 * need to refetch the query each time we receive one of these events. Note that this must also be
 * reference stable.
 */
export function useSocketSearch<K extends SentinelKey, C extends InferSocketResponse<K>>(
  Context: React.Context<DqSocketContextData>,
  tag: K,
  query: any,
  queryEvaluator?: SentinelQueryEvaluator<K>,
) {
  const dispatch = useDispatch();
  const selector = useMemo(() => createSentinelSearchDataByQuerySelector<K>(tag, query), [query, tag]);
  const items = useSelector(selector);
  const [fetched, setFetched] = useState(false);
  const [loadingKey, setLoadingKey] = useState<number | undefined>(undefined);
  const [invalidatedAt, setInvalidatedAt] = useState(0);

  const setInvalidated = useMemo(() => {
    return debounce(() => {
      setInvalidatedAt(Date.now());
    }, 1000);
  }, []);

  const created = useCallback(
    (data: C) => {
      const evaluation = queryEvaluator?.(data, query) ?? SentinelQueryEvaluation.Indeterminate;
      // If queryEvaluator returns "include", associate this entity with the current query.
      const queryKey = evaluation === SentinelQueryEvaluation.Include ? JSON.stringify(query) : undefined;
      dispatch(sentinelActions.appendOne({ key: tag, value: data, query: queryKey }));
      // The created entity may need to be included in these search results.
      // Invalidate the request so that it is fetched again.
      if (evaluation === SentinelQueryEvaluation.Indeterminate) setInvalidated();
    },
    [queryEvaluator, dispatch, query, setInvalidated, tag],
  );

  const updated = useCallback(
    (data: C) => {
      const evaluation = queryEvaluator?.(data, query) ?? SentinelQueryEvaluation.Indeterminate;
      // If queryEvaluator returns "include", associate this entity with the current query.
      const queryKey = evaluation === SentinelQueryEvaluation.Include ? JSON.stringify(query) : undefined;
      dispatch(sentinelActions.appendOne({ key: tag, value: data, query: queryKey }));
      // The entity may have been updated in a way that changes whether it is included in these results.
      // Invalidate the request so that it is fetched again.
      if (evaluation === SentinelQueryEvaluation.Indeterminate) setInvalidated();
    },
    [queryEvaluator, dispatch, query, setInvalidated, tag],
  );

  const deleted = useCallback(
    (data: C) => {
      dispatch(sentinelActions.removeOne({ key: tag, id: data._id }));
    },
    [dispatch, tag],
  );

  const { request, connected } = useSocket(Context, tag, created, updated, deleted);

  const search = useCallback(
    async (searchRequest: any, options?: DqSocketRequestOptions) => {
      const searchResponse = await request<SearchResponse<C>>(`${tag} search`, searchRequest, options);
      dispatch(
        sentinelActions.upsertMany({
          key: tag,
          value: searchResponse.results,
          query: JSON.stringify(searchRequest),
          responseMeta: { relations: searchResponse.relations, totalCount: searchResponse.totalCount },
        }),
      );
      return searchResponse;
    },
    [dispatch, request, tag],
  );

  useEffect(() => {
    const abort = new AbortController();

    const exec = async () => {
      setLoadingKey(invalidatedAt);
      try {
        if (connected && !isNil(query)) {
          await search(query, { signal: abort.signal });
        }
      } catch (err) {
        console.error(`Error in socket search for ${tag}`, err);
        throw err;
      } finally {
        if (abort.signal.aborted) return;

        setFetched(true);
        setLoadingKey(current => {
          if (current === invalidatedAt) return undefined;
          return current;
        });
      }
    };
    exec().catch(e => console.error('failed to run search', e));

    return () => {
      abort.abort();
    };
  }, [connected, search, query, tag, invalidatedAt]);

  return { connected, items, fetched, isLoading: loadingKey !== undefined };
}
